/**
 * `MediaRecorder` emits the browser's preferred container, but the upload route
 * takes wav/mp3 only; 16 kHz mono PCM fits the 5 MB cap and the provider plays it as-is.
 */

export const RECORDING_MAX_SECONDS = 120;
// Under brian's `DISCLOSURE_COMPLETION_DEADLINE_SECONDS` (45s, modules/phone/tasks.py).
export const OUTBOUND_DISCLOSURE_MAX_SECONDS = 30;

const TARGET_RATE = 16_000;

export interface WavRecording {
  stop: () => Promise<File>;
  cancel: () => void;
}

export const isRecordingSupported = (): boolean =>
  typeof MediaRecorder !== "undefined" &&
  typeof OfflineAudioContext !== "undefined" &&
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia;

export const startWavRecording = async (
  deviceId: string | null,
  maxSeconds = RECORDING_MAX_SECONDS,
): Promise<WavRecording> => {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: deviceId ? { deviceId: { exact: deviceId } } : true,
  });
  const release = () => stream.getTracks().forEach((track) => track.stop());
  let recorder: MediaRecorder;
  try {
    recorder = new MediaRecorder(stream);
    recorder.start();
  } catch (error) {
    release();
    throw error;
  }
  const chunks: Blob[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };
  return {
    stop: () =>
      new Promise<File>((resolve, reject) => {
        recorder.onstop = () => {
          release();
          encodeWav(
            new Blob(chunks, { type: recorder.mimeType }),
            maxSeconds,
          ).then(resolve, reject);
        };
        recorder.onerror = () => {
          release();
          reject(new Error("recording failed"));
        };
        recorder.stop();
      }),
    cancel: () => {
      recorder.onstop = null;
      if (recorder.state !== "inactive") recorder.stop();
      release();
    },
  };
};

const encodeWav = async (blob: Blob, maxSeconds: number): Promise<File> => {
  const bytes = await blob.arrayBuffer();
  const context = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await context.decodeAudioData(bytes);
  } finally {
    void context.close();
  }
  // Background tabs may throttle the stop timer; the saved audio still obeys the limit.
  const frames = Math.max(
    1,
    Math.ceil(Math.min(decoded.duration, maxSeconds) * TARGET_RATE),
  );
  const offline = new OfflineAudioContext(1, frames, TARGET_RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const mono = (await offline.startRendering()).getChannelData(0);

  const buffer = new ArrayBuffer(44 + mono.length * 2);
  const view = new DataView(buffer);
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i += 1)
      view.setUint8(offset + i, text.charCodeAt(i));
  };
  ascii(0, "RIFF");
  view.setUint32(4, 36 + mono.length * 2, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, TARGET_RATE, true);
  view.setUint32(28, TARGET_RATE * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  ascii(36, "data");
  view.setUint32(40, mono.length * 2, true);
  for (let i = 0; i < mono.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, mono[i]));
    view.setInt16(
      44 + i * 2,
      sample < 0 ? sample * 0x8000 : sample * 0x7fff,
      true,
    );
  }
  return new File([buffer], "recorded-message.wav", { type: "audio/wav" });
};
